import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Image,
  useWindowDimensions,
  ScrollView,
} from 'react-native';
import { useAuth } from '../context/AuthContext';
import Icon from 'react-native-vector-icons/Feather';
import { useTheme, ThemeColors } from '../context/ThemeContext';
import { useEntranceCascade } from '../components/motion/Motion';

export function LoginScreen() {
  const { login } = useAuth();
  const { colors, isDark } = useTheme();
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const { width } = useWindowDimensions();

  // Responsive breakpoints
  const isMobile = width < 860;
  const isTablet = width >= 860 && width < 1120;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);

  // The welcome text and the sign-in card's parts rise into place in turn.
  const heroRef = useEntranceCascade();
  const cardRef = useEntranceCascade();

  const handleLogin = async () => {
    setError(null);
    setIsLoading(true);
    try {
      await login();
    } catch (err: any) {
      console.error('Sign-in error:', err);
      let errorMsg = err?.message ?? 'Sign-in was cancelled or failed. Please try again.';
      if (err?.name === 'BrowserAuthError' || errorMsg.includes('popup_window_error')) {
        errorMsg = 'Sign-in window was blocked or closed. Please allow popups for this site and try again.';
      }
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ScrollView
      contentContainerStyle={[styles.pageScroll, isMobile && styles.pageScrollMobile]}
      bounces={false}
      showsVerticalScrollIndicator={false}>
      <View style={[styles.page, isMobile && styles.pageMobile]}>

        {/* ================= LEFT SIDEBAR (DESKTOP & TABLET) ================= */}
        {!isMobile && (
          <View style={[styles.leftPanel, isTablet && styles.leftPanelTablet]}>

            {/* Top Brand Header without container border/bg and without parent portal subtitle */}
            <View style={styles.leftBrandHeader}>
              <Image
                source={require('../assets/images/cayman_acadmy_footer_logo.png')}
                style={styles.leftLogoImage}
                resizeMode="contain"
              />
              <View style={styles.leftBrandTextContainer}>
                <Text style={styles.leftInstitutionName}>CAYMAN ACADEMY</Text>
              </View>
            </View>

            {/* Simple, Clean Hero Content */}
            <View ref={heroRef} style={styles.leftBody}>
              <Text style={styles.headline}>
                Stay connected with{'\n'}your child’s school.
              </Text>

              <Text style={styles.subheadline}>
                Attendance, fees, transport tracking, results and messages from your child's school - all in one secure place.
              </Text>
            </View>

            {/* Left Panel Footer */}
            <View style={styles.leftFooterContainer}>
              <Text style={styles.leftCopyright}>
                © 2026 Cayman Academy. All rights reserved.
              </Text>
            </View>

          </View>
        )}

        {/* ================= RIGHT SIGN-IN PANEL ================= */}
        <View style={[styles.rightPanel, isMobile && styles.rightPanelMobile]}>

          {/* Elevated Enterprise Card */}
          <View ref={cardRef} style={[styles.card, isMobile && styles.cardMobile]}>

            {/* Card Brand Crest & Title */}
            <View style={styles.cardHeaderSection}>
              <Image
                source={require('../assets/images/cayman_acadmy_logo.png')}
                style={styles.cardLogoImage}
                resizeMode="contain"
              />
              <View style={styles.headerTitleWrap}>
                <Text style={styles.cardBrandName}>CAYMAN ACADEMY</Text>
              </View>
            </View>

            {/* Welcome Text */}
            <View style={styles.welcomeSection}>
              <Text style={styles.signInLabel}>SIGN IN</Text>
              <Text style={styles.welcomeHeading}>Welcome back</Text>
              <Text style={styles.welcomeSubtitle}>
                Sign in with the email address the school invited to access your parent dashboard.
              </Text>
            </View>

            {/* Error Banner */}
            {error && (
              <View style={styles.errorBox}>
                <Icon name="alert-circle" size={16} color="#DC2626" style={{ marginRight: 8, marginTop: 2 }} />
                <Text style={styles.errorText}>{error}</Text>
              </View>
            )}

            {/* Enterprise Microsoft SSO Button */}
            <TouchableOpacity
              style={[styles.ssoButton, isLoading && styles.ssoButtonDisabled]}
              onPress={handleLogin}
              disabled={isLoading}
              activeOpacity={0.88}>
              {isLoading ? (
                <View style={styles.ssoButtonContent}>
                  <ActivityIndicator color="#ffffff" size="small" style={{ marginRight: 10 }} />
                  <Text style={styles.ssoButtonText}>Connecting to Microsoft...</Text>
                </View>
              ) : (
                <View style={styles.ssoButtonContent}>
                  {/* Microsoft 4-Color Tiles */}
                  <View style={styles.msftLogoBox}>
                    <View style={styles.msftRow}>
                      <View style={[styles.msftTile, { backgroundColor: '#F25022' }]} />
                      <View style={[styles.msftTile, { backgroundColor: '#7FBA00' }]} />
                    </View>
                    <View style={styles.msftRow}>
                      <View style={[styles.msftTile, { backgroundColor: '#00A4EF' }]} />
                      <View style={[styles.msftTile, { backgroundColor: '#FFB900' }]} />
                    </View>
                  </View>
                  <Text style={styles.ssoButtonText}>Sign in with your email</Text>
                  <Icon name="arrow-right" size={15} color="#ffffff" style={{ marginLeft: 8 }} />
                </View>
              )}
            </TouchableOpacity>

            {/* Clean Info Hint */}
            <View style={styles.infoBanner}>
              <Icon name="info" size={14} color={colors.primary} style={{ marginRight: 8, marginTop: 1 }} />
              <Text style={styles.infoBannerText}>
                Only parents invited by the school can sign in. If your email is not recognised, contact the school office to be invited.
              </Text>
            </View>

            {/* Security Guarantee */}
            <View style={styles.securityFooter}>
              <Icon name="lock" size={13} color={colors.textSecondary} style={{ marginRight: 6 }} />
              <Text style={styles.securityFooterText}>
                Your information is protected with secure encryption and kept private.
              </Text>
            </View>

            {/* Help Dropdown Toggle */}
            <View style={styles.helpSection}>
              <TouchableOpacity
                onPress={() => setShowHelp(!showHelp)}
                style={styles.helpToggleBtn}
                activeOpacity={0.7}>
                <Text style={styles.helpToggleText}>Need help signing in?</Text>
                <Icon
                  name={showHelp ? 'chevron-up' : 'chevron-down'}
                  size={13}
                  color={colors.primary}
                  style={{ marginLeft: 4 }}
                />
              </TouchableOpacity>

              {showHelp && (
                <View style={styles.helpDrawer}>
                  <Text style={styles.helpDrawerTitle}>Cayman Academy IT Support</Text>
                  <Text style={styles.helpDrawerBody}>
                    Email: support@caymanacademy.ky{'\n'}
                    Phone: (345) 949-7190 (Mon – Fri)
                  </Text>
                </View>
              )}
            </View>

            {/* Mobile Footer */}
            {isMobile && (
              <Text style={styles.mobileCopyright}>
                © 2026 Cayman Academy. All rights reserved.
              </Text>
            )}

          </View>
        </View>

      </View>
    </ScrollView>
  );
}

const createStyles = (colors: ThemeColors, isDark: boolean) =>
  StyleSheet.create({
    pageScroll: {
      flexGrow: 1,
      minHeight: '100vh' as any,
      backgroundColor: colors.background,
    },
    pageScrollMobile: {
      minHeight: '100%',
    },
    page: {
      flex: 1,
      flexDirection: 'row',
      minHeight: '100vh' as any,
      backgroundColor: colors.background,
    },
    pageMobile: {
      flexDirection: 'column',
      minHeight: '100%',
    },

    /* ================= LEFT SIDEBAR ================= */
    leftPanel: {
      flex: 1,
      minWidth: 420,
      backgroundColor: '#0F2547',
      backgroundImage: 'linear-gradient(135deg, rgb(30, 58, 138) 0%, rgb(22 40 80) 100%)',
      paddingHorizontal: 64,
      paddingVertical: 56,
      justifyContent: 'space-between',
    } as any,
    leftPanelTablet: {
      minWidth: 380,
      paddingHorizontal: 40,
      paddingVertical: 44,
    },

    leftBrandHeader: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    leftLogoImage: {
      width: 90,
      height: 90,
      marginRight: 18,
    },
    leftBrandTextContainer: {
      justifyContent: 'center',
    },
    leftInstitutionName: {
      fontSize: 24,
      fontWeight: '700',
      color: '#FFFFFF',
      letterSpacing: 0.5,
    },

    leftBody: {
      maxWidth: 480,
    },
    headline: {
      fontSize: 44,
      fontWeight: '700',
      color: '#fff',
      lineHeight: 52,
      marginBottom: 20,
    },
    subheadline: {
      fontSize: 15,
      color: '#9EBAE0',
      lineHeight: 24,
    },

    leftFooterContainer: {
      paddingTop: 16,
    },
    leftCopyright: {
      fontSize: 12,
      color: '#6E85A8',
    },

    /* ================= RIGHT PANEL ================= */
    rightPanel: {
      flex: 1,
      backgroundColor: isDark ? '#0B1325' : '#F8FAFC',
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: 40,
      paddingVertical: 48,
    },
    rightPanelMobile: {
      paddingHorizontal: 20,
      paddingVertical: 36,
      backgroundColor: colors.background,
    },

    card: {
      width: '100%',
      maxWidth: 440,
      backgroundColor: colors.card,
      borderRadius: 24,
      borderWidth: 1,
      borderColor: isDark ? '#1E293B' : '#E2E8F0',
      paddingHorizontal: 36,
      paddingVertical: 40,
      boxShadow: isDark
        ? '0 24px 48px -12px rgba(0, 0, 0, 0.6)'
        : '0 20px 40px -15px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.04)' as any,
    },
    cardMobile: {
      paddingHorizontal: 24,
      paddingVertical: 32,
      borderRadius: 20,
    },

    cardHeaderSection: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 28,
      paddingBottom: 20,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    cardLogoImage: {
      width: 82,
      height: 82,
      marginRight: 16,
    },
    headerTitleWrap: {
      flex: 1,
      justifyContent: 'center',
    },
    cardBrandName: {
      fontSize: 20,
      fontWeight: '700',
      color: colors.textPrimary,
      letterSpacing: 0.5,
    },

    welcomeSection: {
      marginBottom: 26,
    },
    signInLabel: {
      fontSize: 11,
      fontWeight: '700',
      color: colors.primary,
      letterSpacing: 1.2,
      marginBottom: 6,
    },
    welcomeHeading: {
      fontSize: 28,
      fontWeight: '700',
      color: colors.textPrimary,
      letterSpacing: -0.5,
      marginBottom: 8,
    },
    welcomeSubtitle: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 21,
    },

    errorBox: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      backgroundColor: '#FEF2F2',
      borderWidth: 1,
      borderColor: '#FCA5A5',
      borderRadius: 12,
      padding: 12,
      marginBottom: 20,
    },
    errorText: {
      flex: 1,
      fontSize: 12,
      color: '#B91C1C',
      lineHeight: 17,
    },

    ssoButton: {
      backgroundColor: colors.primary,
      borderRadius: 14,
      paddingVertical: 15,
      paddingHorizontal: 18,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 52,
      boxShadow: '0 10px 20px -5px rgba(27, 112, 209, 0.35)' as any,
    },
    ssoButtonDisabled: {
      opacity: 0.72,
    },
    ssoButtonContent: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    msftLogoBox: {
      width: 17,
      height: 17,
      justifyContent: 'space-between',
      marginRight: 11,
    },
    msftRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    msftTile: {
      width: 7.5,
      height: 7.5,
      borderRadius: 1,
    },
    ssoButtonText: {
      fontSize: 15,
      fontWeight: '700',
      color: '#FFFFFF',
      letterSpacing: 0.3,
    },

    infoBanner: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      backgroundColor: colors.primaryLight,
      borderWidth: 1,
      borderColor: isDark ? 'rgba(27, 112, 209, 0.3)' : '#DBEAFE',
      borderRadius: 12,
      padding: 12,
      marginTop: 20,
    },
    infoBannerText: {
      flex: 1,
      fontSize: 12,
      color: isDark ? '#93C5FD' : '#1E40AF',
      lineHeight: 17,
    },

    securityFooter: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: 22,
    },
    securityFooterText: {
      fontSize: 12,
      color: colors.textSecondary,
    },

    helpSection: {
      marginTop: 14,
      alignItems: 'center',
    },
    helpToggleBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 6,
      paddingHorizontal: 8,
    },
    helpToggleText: {
      fontSize: 12,
      fontWeight: '600',
      color: colors.primary,
    },
    helpDrawer: {
      marginTop: 8,
      width: '100%',
      backgroundColor: isDark ? '#1E293B' : '#F8FAFC',
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 12,
      padding: 12,
    },
    helpDrawerTitle: {
      fontSize: 12,
      fontWeight: '700',
      color: colors.textPrimary,
      marginBottom: 4,
    },
    helpDrawerBody: {
      fontSize: 12,
      color: colors.textSecondary,
      lineHeight: 17,
    },

    mobileCopyright: {
      fontSize: 11,
      color: colors.textSecondary,
      marginTop: 20,
      textAlign: 'center',
    },
  });
